import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button
} from '@mui/material';

import * as Yup from 'yup';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import request from 'src/utils/request';

const attachmentSchema = Yup.object({
  documentName: Yup.string()
    .trim()
    .required('Document Name is required'),

  file: Yup.mixed()
    .required('File is required')
    .test(
      'fileFormat',
      'Only .jpg, .jpeg, .png, .pdf, .doc, .docx, .xls and .xlsx files are allowed',
      (value) => {
        // Let required() handle empty files
        if (!value) return true;

        const extension = value.name
          ?.split('.')
          .pop()
          ?.toLowerCase();

        return [
          'jpg',
          'jpeg',
          'png',
          'pdf',
          'doc',
          'docx',
          'xls',
          'xlsx'
        ].includes(extension);
      }
    )
});

const createNewRow = () => ({
  tempId: `tmp_${Date.now()}_${Math.random()}`,

  documentName: '',
  file: null,

  isEditing: true,
  isNew: true,

  originalDocumentName: '',
  originalFile: null
});

const PolicyAttachments = ({ policyForm }) => {
  const [rows, setRows] = useState([createNewRow()]);
  const [errors, setErrors] = useState({});
  const [documentNames, setDocumentNameList] = useState([]);

  useEffect(() => {
    getDocumentNameList();
  }, []);

  const getDocumentNameList = async () => {
    request({
      url: 'documentName/getDocumentNameList',
      method: 'POST',
      data: {
        search: null,
        modules: ['Policy'],
        offset: 0,
        limit: 50000,
        orderColumn: 'id',
        orderDirection: 'DESC'
      }
    }).then(
      (response: any) => {
        setDocumentNameList(
          response?.documentNameData || []
        );
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const syncFormikValues = (updatedRows) => {
    const validRows = updatedRows.filter(
      (row) => row.file
    );

    policyForm.setFieldValue(
      'policyFile',
      validRows.map((row) => row.file)
    );

    policyForm.setFieldValue(
      'documentDetails',
      validRows.map((row) => ({
        name: row.documentName
      }))
    );
  };

  useEffect(() => {
    syncFormikValues(rows);
  }, [rows]);

  const updateRow = (
    index,
    field,
    value
  ) => {
    const clone = [...rows];
    clone[index][field] = value;
    setRows(clone);
  };

  const validateRow = async (
    row,
    index
  ) => {
    try {
      await attachmentSchema.validate(row, {
        abortEarly: false
      });

      setErrors((prev) => ({
        ...prev,
        [index]: {}
      }));

      return true;
    } catch (error) {
      const rowErrors = {};

      error.inner.forEach((err) => {
        rowErrors[err.path] = err.message;
      });

      setErrors((prev) => ({
        ...prev,
        [index]: rowErrors
      }));

      return false;
    }
  };

  const saveRow = async (
    row,
    index
  ) => {
    const valid = await validateRow(
      row,
      index
    );

    if (!valid) return;

    const clone = [...rows];

    clone[index].isEditing = false;
    clone[index].isNew = false;

    setRows(clone);
  };

  const editRow = (index) => {
    const clone = [...rows];

    clone[index].originalDocumentName =
      clone[index].documentName;

    clone[index].originalFile =
      clone[index].file;

    clone[index].isEditing = true;

    setRows(clone);
  };

  const cancelEdit = (index) => {
    const clone = [...rows];

    if (clone[index].isNew) {
      clone.splice(index, 1);

      setRows(
        clone.length
          ? clone
          : [createNewRow()]
      );

      return;
    }

    clone[index].documentName =
      clone[index]
        .originalDocumentName;

    clone[index].file =
      clone[index].originalFile;

    clone[index].isEditing = false;

    setRows(clone);
  };

  const deleteRow = (index) => {
    const clone = [...rows];

    clone.splice(index, 1);

    setRows(
      clone.length
        ? clone
        : [createNewRow()]
    );
  };

  const addRow = async (
    row,
    index
  ) => {
    const valid = await validateRow(
      row,
      index
    );

    if (!valid) return;

    setRows((prev) => [
      ...prev,
      createNewRow()
    ]);
  };

  const IMAGE_FORMATS = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ];

  const PLACEHOLDER_IMAGE =
    'https://p.kindpng.com/picc/s/160-1608792_circle-document-icon-png-transparent-png.png';

  const getImagePreview = (row) => {
    if (!row.file) {
      return PLACEHOLDER_IMAGE;
    }

    if (
      row.file instanceof File &&
      IMAGE_FORMATS.includes(
        row.file.type
      )
    ) {
      return URL.createObjectURL(
        row.file
      );
    }

    return PLACEHOLDER_IMAGE;
  };

  const SUPPORTED_FORMATS = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    '.png',
    '.jpeg',
    '.jpg',
    '.gif',
    '.webp',
    '.doc',
    '.docx',
    '.csv',
    '.xlsx',
    '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  const isInvalidFile = (file) => {
    return (
      file &&
      !SUPPORTED_FORMATS.includes(
        file.type
      ) &&
      !SUPPORTED_FORMATS.includes(
        '.' +
        file.name
          .split('.')
          .pop()
          .toLowerCase()
      )
    );
  };

  const canShowAddButton = (row) => {
    return (
      row.documentName?.trim() &&
      row.file &&
      !row.isEditing
    );
  };

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <Typography
        variant="h6"
        mb={2}
      >
        Policy Attachments
      </Typography>

      {rows.map((row, index) => (
        <Box
          key={row.tempId || index}
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid #E0E0E0',
            borderRadius: 2
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="flex-start"
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <TextField
                select
                fullWidth
                size="small"
                label={
                  <>
                    Document Name <span style={{ color: 'red' }}>*</span>
                  </>
                }
                value={
                  row.documentName
                }
                disabled={
                  !row.isEditing
                }
                error={Boolean(
                  errors?.[index]
                    ?.documentName
                )}
                helperText={
                  errors?.[index]
                    ?.documentName || ''
                }
                onChange={(e) =>
                  updateRow(
                    index,
                    'documentName',
                    e.target.value
                  )
                }
              >
                <MenuItem value="">
                  Select Document
                </MenuItem>

                {documentNames.map(
                  (doc) => (
                    <MenuItem
                      key={doc.id}
                      value={doc.name}
                    >
                      {doc.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
            >
              {row.isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      height: '37px',
                      justifyContent: 'flex-start',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        component="span"
                        title={row.file?.name}
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'left',
                          minWidth: 0

                        }}
                        style={{ fontWeight: 'bold' }}
                      >
                        {row.file ? row.file.name : 'Choose File'}
                      </Typography>

                      <span
                        style={{
                          color: 'red',
                          marginLeft: '5px',
                          fontSize: '13px'
                        }}
                      >
                        *
                      </span>

                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 'bold',
                          marginLeft: '10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        (.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx)
                      </span>
                    </Box>

                    <input
                      hidden
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];

                        if (!file) return;

                        updateRow(index, 'file', file);
                      }}
                    />
                  </Button>

                  {errors?.[index]
                    ?.file && (
                      <Typography
                        color="error"
                        fontSize={13}
                        fontWeight="bold"
                        mt={0.5}
                      >
                        {
                          errors[index]
                            .file
                        }
                      </Typography>
                    )}
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '40px'
                  }}
                >
                  <img
                    src={getImagePreview(
                      row
                    )}
                    alt={
                      row.documentName
                    }
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: 'contain'
                    }}
                  />

                  {/* <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      maxWidth: 120,
                      overflow:
                        'hidden',
                      textOverflow:
                        'ellipsis',
                      whiteSpace:
                        'nowrap'
                    }}
                  >
                    {row.file?.name}
                  </Typography> */}

                  {isInvalidFile(
                    row.file
                  ) && (
                      <Typography
                        color="error"
                        sx={{
                          mt: 0.5,
                          whiteSpace:
                            'nowrap',
                          fontSize:
                            '13px',
                          fontWeight:
                            'bold'
                        }}
                      >
                        Unsupported file
                      </Typography>
                    )}
                </Box>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
              >
                {row.isEditing ? (
                  <>
                    <Tooltip title="Save">
                      <IconButton
                        onClick={() =>
                          saveRow(
                            row,
                            index
                          )
                        }
                      >
                        <SaveIcon color="primary" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Cancel">
                      <IconButton
                        onClick={() =>
                          cancelEdit(
                            index
                          )
                        }
                      >
                        <CloseIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() =>
                        editRow(index)
                      }
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                )}

                {!row.isEditing && <Tooltip title="Delete">
                  <IconButton
                    onClick={() =>
                      deleteRow(index)
                    }
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Tooltip>}

                {index ===
                  rows.length - 1 && canShowAddButton(row) && (
                    <Tooltip title="Add Row">
                      <IconButton
                        onClick={() =>
                          addRow(
                            row,
                            index
                          )
                        }
                      >
                        <AddCircleOutlineIcon color="success" />
                      </IconButton>
                    </Tooltip>
                  )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Card>
  );
};

export default PolicyAttachments;